import { createContext, useEffect, useState, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import { AUTH_URL, LOGIN_URL, accountSettingsUrl, useSession, signOut } from './lib/auth-client.js';
import { MAX_ACCOUNTS, chooserUrl, listDeviceAccounts, switchTo, type DeviceAccount } from './lib/device-accounts.js';
import { ThemeControl } from './components/ThemeControl.js';
import { hasAdminRole } from './lib/roles.js';
import {
  CapabilityProvider,
  firstAllowedPath,
  homeOrder,
  holdsAny,
  screenGate,
  useCan,
  useCanAny,
  useCapabilitiesLoading,
  useHeldCapabilities,
} from './lib/capabilities.js';
import {
  allowBounce,
  clearSignedOut,
  markSignedOut,
  shouldBounce,
  signedOutDeliberately,
  suppressBounce,
} from './lib/bounce.js';
import { useRoute } from './lib/route.js';
import { Overview } from './screens/Overview.js';
import { Users } from './screens/Users.js';
import { Agents } from './screens/Agents.js';
import { Testers } from './screens/Testers.js';
import { Agreements } from './screens/Agreements.js';
import { Applications } from './screens/Applications.js';
import { SecurityGroups } from './screens/SecurityGroups.js';
import { Activity } from './screens/Activity.js';
import { Notifications } from './screens/Notifications.js';
import { Developer } from './screens/Developer.js';
import { SystemHealth } from './screens/SystemHealth.js';
import { CiFleet } from './screens/CiFleet.js';
import { Support } from './screens/Support.js';
import { Redirect, SupportConfiguration, movedSupportPath } from './screens/SupportConfiguration.js';
import { Knowledgebase } from './screens/Knowledgebase.js';
import { Subscriptions } from './screens/Subscriptions.js';
import { useFleetAlerts, type FleetAlertsState } from './lib/useFleetAlerts.js';
import { needsAttention, useReportCounts } from './lib/useReportCounts.js';
import { ReportBadge } from './components/ReportBadge.js';
import { Reports } from './screens/Reports.js';
import { Menu, MenuItem } from './components/Screen.js';
import { Avatar } from './components/ui.js';
import {
  Logo,
  OverviewIcon,
  UsersIcon,
  GroupsIcon,
  AppsIcon,
  DocIcon,
  ActivityIcon,
  MailIcon,
  SupportIcon,
  FlagIcon,
  SettingsIcon,
  BookIcon,
  SubscriptionsIcon,
  AgentIcon,
  DeveloperIcon,
  TestersIcon,
  FleetIcon,
  HealthIcon,
} from './components/icons.js';

/**
 * The shell: session gate, navigation, and which screen is showing.
 *
 * Everything protective is on the server. This file decides what a person is
 * told, which is a different job and the one the old portal did badly — it
 * rendered a failed session lookup as "signed out" and sent people to sign in
 * again, forever, while the API was unreachable.
 */

/**
 * ── EACH ENTRY NAMES THE READ ITS SCREEN NEEDS (org-admin#134) ───────
 *
 * `anyOf` is the capability, as auth spells it, that the screen's own list
 * endpoint is guarded by. An entry the session holds none of is not rendered,
 * and neither is its screen when reached by URL. Overview has none: it is the
 * shell, and `console:read` is what lets anybody into the portal at all.
 *
 * This is still no role rule. Which ROLE sees what is auth's `lib/access.ts`;
 * this list only says which permission each screen reads with, which is a fact
 * about the screen. Jon's 2026-09-16 pass (Support Admin without the developer
 * screens, Legal Admin with only Overview, Agreements and Notifications) is
 * expressed entirely in auth's grants.
 *
 * The nav, in two groups: the screens you WORK IN and the ones you CHECK.
 *
 * The line was already here in prose. The flat list ended with a comment
 * explaining that System health sits last because "everything above it is a
 * thing an admin came here to do something about; this one is a thing they came
 * to check" — a real distinction held only by ordering, which is the weakest
 * way to express it. Ordering says "later", not "different in kind", and it
 * degrades silently the moment somebody appends a screen.
 *
 * Credentials belongs on the same side of that line as System health: it is
 * infrastructure you go and look at, not a person or an application you act on.
 * With CI fleet arriving next (org-admin#96) the check-side group is three
 * items, which is where a divider stops being decoration.
 *
 * ORDER INSIDE EACH GROUP IS UNCHANGED, deliberately. Regrouping already moves
 * things under people's hands; reordering as well would mean nobody's muscle
 * memory survives.
 *
 * Adding CI fleet is one line in the Developer group (called Platform until 15
 * Sep, when Applications moved into it) and nothing else.
 */
const NAV_GROUPS = [
  {
    /* No heading. A label over the first six would be chrome that reads "the
       app" — the top group needs no name because it is the default, and the
       divider below it is what actually carries the meaning. */
    label: null,
    items: [
      /* Gated on the sections worth a dashboard: anything besides the
         Agreements figure, which alone made Legal Admin's Overview one number
         on an empty page (Jon, 2026-09-16, auth#518). A session without any of
         these lands on its first screen instead; see `HomeRedirect`. */
      {
        path: '',
        label: 'Overview',
        Icon: OverviewIcon,
        anyOf: ['user:list', 'application:read', 'credential:read', 'activity:read'],
      },
      { path: 'users', label: 'Users', Icon: UsersIcon, anyOf: ['user:list'] },
      /* Directly under Users, because a group IS a set of them and the two screens
         answer halves of one question — "what can this person reach" and "who does
         this entitlement reach". */
      { path: 'security-groups', label: 'Security groups', Icon: GroupsIcon, anyOf: ['securityGroup:read'] },
      { path: 'activity', label: 'Activity', Icon: ActivityIcon, anyOf: ['activity:read'] },
      /* Beside Activity: both are records of what the platform did, and support
         reaches for them together ("did the reset email go out?"). Either
         read opens it, because each of its tabs has its own (org-admin#131). */
      {
        path: 'notifications',
        label: 'Notifications',
        Icon: MailIcon,
        anyOf: ['notification:read', 'optOut:read'],
      },
      /* What people pay for, and the promotional grants staff gave
         (org-admin#162). `subscription:read`: Support, Auth and Global Admin.
         Needs an auth that serves /admin/subscriptions (auth#569): deploy it
         first, or the screens show an error. */
      { path: 'subscriptions', label: 'Subscriptions', Icon: SubscriptionsIcon, anyOf: ['subscription:read'] },
      /* "Legal", not "Agreements" (Jon, 2026-10-15): it holds policies, notices
         and agreements, everything ridgewill.com/legal shows. Below
         Subscriptions. `#/agreements` still opens it; see the Screen switch. */
      { path: 'legal', label: 'Legal', Icon: DocIcon, anyOf: ['agreement:read'] },
    ],
  },
  {
    /* "Support", its own group like Developer (Jon, 2026-10-11): the queue and
       feedback, the knowledgebase, and how the desk is configured. `support:read`
       is auth's affordance for exactly the roles the support service lets in
       (auth#563), so a person who sees these can work them. */
    label: 'Support',
    items: [
      /* `#/support` stays the queue, so every ticket link already sent keeps working. */
      { path: 'support', label: 'Tickets & feedback', Icon: SupportIcon, anyOf: ['support:read'] },
      /* In-app abuse reports, their own queue (support#87, Jon 2026-10-24).
         `user:ban`, not `support:read`: support serves reports only to Support
         Admin and up, never the AI support agent, and `user:ban` is held by
         exactly those roles. Change the two together. */
      { path: 'reports', label: 'Reports', Icon: FlagIcon, anyOf: ['user:ban'] },
      { path: 'knowledgebase', label: 'Knowledgebase', Icon: BookIcon, anyOf: ['support:read'] },
      { path: 'support-configuration', label: 'Configuration', Icon: SettingsIcon, anyOf: ['support:read'] },
    ],
  },
  {
    /* "Developer", with Applications first (Jon, 15 Sep): registering and
       configuring an app is developer work, and sits with the credentials,
       health and CI screens it is managed alongside. */
    label: 'Developer',
    items: [
      { path: 'applications', label: 'Applications', Icon: AppsIcon, anyOf: ['application:read'] },
      { path: 'developer', label: 'Credentials', Icon: DeveloperIcon, anyOf: ['credential:read'] },
      /* Agents, with the credentials rather than with Users (Jon, 21 Sep).
         An agent account is administered like any other, but it is something
         the platform RUNS rather than somebody who signs in, and the people who
         create and revoke its credential are the people who need it. Gated on
         `user:list` because it is a listing of accounts and nothing more. */
      { path: 'agents', label: 'Agents', Icon: AgentIcon, anyOf: ['user:list'] },
      /* Every tester card, to query and turn into security groups
         (RidgeWill/platform#56). `tester:read`: Auth Admin and Global Admin,
         because a card holds a person's email and devices. */
      { path: 'testers', label: 'Testers', Icon: TestersIcon, anyOf: ['tester:read'] },
      /* Both split out of `console:read` in auth for org-admin#134, so they need
         an auth that grants them. Deploy auth first: a portal ahead of it hides
         these two from everybody, Global Admin included. */
      { path: 'system-health', label: 'System health', Icon: HealthIcon, anyOf: ['systemHealth:read'] },
      { path: 'ci-fleet', label: 'CI fleet', Icon: FleetIcon, anyOf: ['fleet:read'] },
    ],
  },
] as const;

export function App() {
  const { data: session, isPending, error } = useSession();

  if (isPending) {
    return (
      <main style={styles.centre}>
        <p style={styles.muted}>Checking your session…</p>
      </main>
    );
  }

  /**
   * A FAILED session lookup is not a signed-out user — auth#32, the same defect
   * this project already shipped once on the login screen. `useSession` reports
   * a failed request as `data: null`, so collapsing the two states tells an
   * admin their session ended when the API is unreachable or CORS is
   * misconfigured, and sends them to sign in again over and over.
   */
  if (error) {
    return (
      <main style={styles.centre}>
        <h1 style={styles.h1}>Can&rsquo;t reach the admin API</h1>
        <p style={styles.muted}>
          {error.message || 'The request failed.'} This is a problem with the service or the
          connection, not with your account.
        </p>
        <p style={{ ...styles.muted, marginTop: '1rem', fontSize: '.85rem' }}>
          Calling <code>{AUTH_URL || '(VITE_AUTH_URL is not set)'}</code>
        </p>
      </main>
    );
  }

  if (!session) return <SignedOut />;

  /**
   * Role checked here for the MESSAGE, not for the protection.
   *
   * The server refuses every admin endpoint without the role — that is the
   * actual control, and it answers 404 so the surface is not advertised. Which
   * means a signed-in non-admin would otherwise see a portal full of "not
   * found" and reasonably conclude the service is broken. This turns that into
   * a sentence that tells them what is actually true.
   */
  if (!hasAdminRole(session.user.role)) {
    /**
     * NOT a redirect, unlike the branch above. This person has a valid session —
     * sending them to sign in again would ask them to fix something that is not
     * broken, and they would arrive straight back here, which is the redirect
     * loop with an extra step and a misleading explanation attached.
     */
    return (
      <SignInPrompt reason={`${session.user.email} is signed in, but does not have admin access.`} />
    );
  }

  return (
    <>
      <BounceReset />
      <Shell
        id={session.user.id}
        name={session.user.name || session.user.email}
        image={session.user.image}
        role={session.user.role ?? ''}
      />
    </>
  );
}

/**
 * Signed out, and sent straight to sign in.
 *
 * The prompt this replaces was a page whose only content was a link to the page
 * you were trying to reach. It is still here, and it is what the SECOND arrival
 * renders — see lib/bounce.ts for why one automatic redirect is a convenience
 * and two in a row is a browser shuttling between origins forever.
 *
 * `replace`, not `assign`: the entry being navigated away from would immediately
 * navigate again, so leaving it in history makes Back a trap rather than a way
 * out.
 *
 * In an effect rather than in render, because navigating is a side effect on the
 * document and React may render this twice — under StrictMode it certainly does.
 */
/** Long enough to read "You have been signed out" and no longer. */
export const SIGNED_OUT_DELAY_MS = 2000;

function SignedOut() {
  const justSignedOut = signedOutDeliberately();
  const bounce = shouldBounce();

  useEffect(() => {
    /**
     * PRESSED SIGN OUT: say so, then go to sign-in anyway.
     *
     * This portal used to stop here, on the reasoning that sending somebody
     * straight back to the form undoes what they just asked for. Jon overruled
     * it (2026-09-09): a signed-out person's next destination IS the sign-in
     * page, and the screen they got instead was a page whose only content is a
     * link to it. The pause is what keeps the sign-out visible -- it confirms
     * the thing happened rather than pretending it did not.
     *
     * `clearSignedOut` as the redirect LEAVES, so a trip back that produces no
     * session falls through to the honest screen rather than looping.
     */
    if (justSignedOut) {
      const timer = setTimeout(() => {
        clearSignedOut();
        window.location.replace(signInUrl());
      }, SIGNED_OUT_DELAY_MS);
      return () => clearTimeout(timer);
    }
    if (!bounce) return;
    suppressBounce();
    window.location.replace(signInUrl());
  }, [justSignedOut, bounce]);

  if (justSignedOut) {
    return (
      <main style={styles.centre}>
        <h1 style={styles.h1}>Signed out</h1>
        <p style={styles.muted}>You have been signed out. Taking you to the sign-in page.</p>
        <p style={{ marginTop: '1.5rem' }}>
          {/* A link as well as the timer, so this is never a wait with no way
              out — a blocked redirect leaves somebody with something to press. */}
          <a href={signInUrl()} style={styles.link}>
            Sign in
          </a>
        </p>
      </main>
    );
  }

  if (bounce) return <main style={styles.centre} />;

  return (
    <SignInPrompt
      reason="You are signed out. If you just tried to sign in and arrived back here, this site's cookies are most likely being blocked."
    />
  );
}

/** Renders nothing; re-arms the redirect for the next signed-out arrival in this
 *  tab. A component rather than a call in `App`, because `App` returns early
 *  several times before this point and a hook cannot live after those. */
function BounceReset() {
  useEffect(() => allowBounce(), []);
  return null;
}

function SignInPrompt({ reason }: { reason: string }) {
  return (
    <main style={styles.centre}>
      <h1 style={styles.h1}>Ridgewill Admin</h1>
      <p style={styles.muted}>{reason}</p>
      <p style={{ marginTop: '1.5rem' }}>
        {/* A full-page navigation, not a fetch: signing in sets a cookie, and
            that has to happen on the auth service's own origin. */}
        <a href={signInUrl()} style={styles.link}>
          Sign in
        </a>
      </p>
    </main>
  );
}

/**
 * Sign in, and come back HERE.
 *
 * Without `returnTo` this was a one-way trip: you signed in successfully and
 * landed on the auth service's placeholder page, which says there is nowhere to
 * send you — leaving a signed-in admin looking at a different site and
 * reasonably concluding the portal had not let them in (auth#59).
 *
 * `href` and not `origin`, so a deep link survives. Being signed out while
 * reading one account's activity should return you to that account, not to the
 * top of the portal.
 *
 * The auth service DECIDES whether to honour this — it checks the destination
 * against its own trusted list and redirects to `/` if it does not recognise it.
 * Which is why building this link here is safe: a login page that navigates to
 * whatever a parameter says is an open redirector, and this one does not do the
 * navigating.
 *
 * ── THE SLUG IS THIS APP'S REGISTERED NAME ───────────────────────────
 *
 * RidgeWill/auth#86 made `returnTo` accept an app name, so the sign-in page
 * shows `?returnTo=org-admin&at=/users/abc123` rather than an encoded URL. That
 * page is the front door for end users now, and an address bar full of
 * percent-encoding is what a phishing page looks like.
 *
 * `org-admin` is our `Application.slug` — registered in this very portal, marked
 * PERMANENT there because it is already stamped into token audiences and
 * `Agreement.appSlug`. The auth service resolves it through a matching name in
 * its own TRUSTED_ORIGINS.
 *
 * THIS APP IS THE REASON THE NAME IS DECLARED RATHER THAN DERIVED. Deriving it
 * from the hostname is the tempting alternative and needs no configuration —
 * but `admin-dev.ridgewill.com` reduces to `admin`, and this application is
 * registered as `org-admin`. A derived scheme would give the platform two names
 * for one app, and this is the app it would happen to first.
 *
 * A constant rather than `import.meta.env`, because the slug names the
 * APPLICATION and not the deployment: it is identical in every environment, so a
 * build-time variable could only introduce a way to be wrong.
 */
const APP_SLUG = 'org-admin';

export function signInUrl(here: string = window.location.href): string {
  // LOGIN_URL, not AUTH_URL. The admin deployment refuses `/sign-in/email` on
  // purpose, so a link there is a form that cannot submit. See auth-client.ts.
  return `${LOGIN_URL}/login?${returnQuery(here)}`;
}

/** `returnTo=org-admin&at=<path>`: back to this page, by this app's name. */
function returnQuery(here: string = window.location.href): string {
  const url = new URL(here);
  // `pathname + search + hash`, so a deep link survives: being signed out while
  // reading one account's activity should return you to that account rather than
  // to the top of the portal. A slug alone lands everyone on the root.
  const at = `${url.pathname}${url.search}${url.hash}`;
  return `returnTo=${APP_SLUG}&at=${encodeURIComponent(at)}`;
}

/**
 * Where a screen's filter row and search render.
 *
 * A screen owns its own controls — which pills, which placeholder, what a chip
 * change refetches — so they cannot be lifted into the shell without the shell
 * knowing about all six. A portal keeps the ownership where it belongs and moves
 * only the DOM.
 *
 * `null` until the header mounts, which is one extra render before first paint
 * rather than a flash: React runs the ref callback during commit, so the state
 * update lands before the browser draws.
 */
export const HeaderSlot = createContext<HTMLElement | null>(null);

/* `id` and not the email is what the avatar colour is hashed from, matching the
   record header — so the same person is the same colour in both places, and the
   colour survives an address change. */
type ShellProps = { id: string; name: string; image?: string | null; role: string };

/**
 * The provider OUTSIDE the frame, so the frame's own hooks can read it
 * (org-admin#134). It used to sit inside the frame's JSX, where only children
 * saw it, and the fleet poll below could not ask whether the session may see
 * the fleet at all.
 */
function Shell(props: ShellProps) {
  return (
    <CapabilityProvider>
      <ShellFrame {...props} />
    </CapabilityProvider>
  );
}

function ShellFrame({ id, name, image, role }: ShellProps) {
  /** Every account this browser holds; null until known, or when it cannot be. */
  const [held, setHeld] = useState<DeviceAccount[] | null>(null);
  useEffect(() => {
    let live = true;
    void listDeviceAccounts().then((list) => {
      if (live) setHeld(list);
    });
    return () => {
      live = false;
    };
  }, [id]);
  const others = (held ?? []).filter((a) => a.user.id !== id);
  const { segments, query, navigate } = useRoute();
  const section = segments[0] ?? '';
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  // One poll for the nav's count and the CI fleet banner (org-admin#97). It
  // signs in to fleet-api only while CI fleet is open; see the hook. And only
  // once capabilities have answered `fleet:read` (org-admin#134): signing in
  // starts a redirect, and a role auth will refuse a fleet token must not be
  // sent on it by a typed URL.
  const capabilitiesLoading = useCapabilitiesLoading();
  const mayFleet = useCan('fleet:read');
  const fleetAlertState = useFleetAlerts(section === 'ci-fleet' && !capabilitiesLoading && mayFleet);
  const fleetAlertCount = fleetAlertState?.alerts.length ?? 0;
  const fleetAlertDanger = fleetAlertState?.alerts.some((a) => a.tone === 'danger') ?? false;
  // The Reports badge (support#87); see the hook for when it signs in.
  const mayReports = useCan('user:ban');
  const reportCounts = useReportCounts(
    !capabilitiesLoading && mayReports,
    ['support', 'reports', 'knowledgebase', 'support-configuration'].includes(section),
  );

  /**
   * PHONE WIDTH (support#87). Jon handles urgent work from a phone when no
   * desktop is near, often from an alert email, and a 232px rail on a 375px
   * screen left the page a sliver. Below 760px (styles.css, `.rw-shell`) the
   * rail becomes a drawer behind a menu button, which carries the Reports badge
   * so a blinking child-safety report is not hidden inside a closed menu.
   * Choosing a screen closes it. Desktop is unchanged: the classes do nothing
   * above the breakpoint.
   */
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [segments.join('/')]);

  return (
    <HeaderSlot.Provider value={headerSlot}>
    <div style={styles.page} className="rw-shell" data-menu={menuOpen ? 'open' : 'closed'}>
      <div className="rw-scrim" onClick={() => setMenuOpen(false)} aria-hidden />
      <nav style={styles.sidebar} className="rw-sidebar" id="rw-sidebar">
        <div style={styles.brand}>
          <Logo size={24} />
          <span style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.01em' }}>Ridgewill</span>
        </div>

        {/*
          ONLY THE LINKS SCROLL (org-admin#9).

          The sidebar used to be one scrolling column, so a nav long enough to
          overflow took the avatar with it — and the avatar is where "Sign out"
          lives, which is the one control that should never need hunting for.

          `minHeight: 0` is what makes this work at all: a flex child's default
          minimum size is its content, so without it this refuses to shrink and
          the overflow moves back up to the sidebar. The brand and the identity
          block are `flex: none`, so they hold their ends.
        */}
        <div style={styles.navScroll}>
          {NAV_GROUPS.map((group) => {
            /**
             * `role="group"` with `aria-labelledby`, so the heading is not
             * merely a styled line of text.
             *
             * Sighted admins get the grouping from the divider and the label.
             * Without this, a screen reader gets neither — it reads eight links
             * in a row and the division a sighted person can see does not exist
             * for anyone else. The unlabelled top group takes no role: a group
             * with no accessible name adds a boundary announcement that says
             * nothing.
             */
            const headingId = group.label ? `rw-nav-${group.label.toLowerCase()}` : undefined;
            return (
              <NavGroupGate
                key={group.label ?? 'main'}
                gates={group.items.map((item) => ('anyOf' in item ? item.anyOf : undefined))}
              >
              <div
                role={group.label ? 'group' : undefined}
                aria-labelledby={headingId}
                style={group.label ? styles.navGroupDivided : undefined}
              >
                {group.label ? (
                  <div id={headingId} style={styles.navGroupLabel}>
                    {group.label}
                  </div>
                ) : null}

                {group.items.map((item) => {
                  const { path, label, Icon } = item;
                  const active = path === section;
                  return (
                    <NavGate key={path} anyOf={'anyOf' in item ? item.anyOf : undefined}>
                    <a
                      /* A real href, so the browser's own affordances work — middle
                         click, copy link, and a status bar that shows where it goes.
                         An onClick-only div has none of that. */
                      href={`#/${path}`}
                      className={active ? undefined : 'rw-navhover'}
                      style={{ ...styles.navLink, ...(active ? styles.navLinkActive : {}) }}
                      aria-current={active ? 'page' : undefined}
                    >
                      {/* The turquoise rail, not a background swap. An active row that
                          only changes shade is indistinguishable from a hovered one,
                          which is the state immediately below the pointer. */}
                      {active ? <span style={styles.navRail} /> : null}
                      <Icon size={18} />
                      {label}
                      {path === 'ci-fleet' && fleetAlertCount > 0 ? (
                        <span
                          style={{
                            ...styles.navCount,
                            background: fleetAlertDanger ? C.danger : C.warnDot,
                          }}
                          aria-label={`${fleetAlertCount} CI fleet alert${fleetAlertCount === 1 ? '' : 's'}`}
                        >
                          {fleetAlertCount}
                        </span>
                      ) : null}
                      {path === 'reports' && reportCounts && reportCounts.active > 0 ? (
                        <ReportBadge count={reportCounts.active} urgent={needsAttention(reportCounts)} />
                      ) : null}
                    </a>
                    </NavGate>
                  );
                })}
              </div>
              </NavGroupGate>
            );
          })}
        </div>

        {/*
          The avatar block is a MENU, not decoration.

          It rendered as an inert row with a Sign out button beneath it, which is
          the one place people reach for and the one place nothing happened.
          What is inside is shorter than the old portal's — user settings belong
          to the account portal (#10), not here — and a menu with one item is
          still worth building: it is where the second lands, and the avatar
          being clickable is the affordance people try first.
        */}
        <div style={styles.identity}>
          <Menu
            label="Account menu"
            align="left"
            /* UPWARDS: the avatar is the last thing in the sidebar, so a menu
               opening down hangs off the bottom of the window (org-admin#9). */
            placement="up"
            trigger={
              <span style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                {/*
                  THE SIGNED-IN PERSON'S OWN PICTURE, if they have set one
                  (Jon, 2026-08-28). It was always a monogram, which was the only
                  option until `/admin/users` started carrying `image` — and the
                  one account guaranteed to be looking at this block is the one
                  most likely to notice their own photograph missing from it.

                  The same component the record header uses, so the colour, the
                  initials and the broken-image fallback cannot drift between the
                  two places an avatar appears. The ring went with it.
                */}
                <Avatar id={id} label={name} image={image} size={32} />
                <span style={{ minWidth: 0, lineHeight: 1.3, flex: 1 }}>
                  <span style={styles.identityName}>{name}</span>
                  <span style={styles.identityRole}>{role || 'no role'}</span>
                </span>
                <span style={{ color: C.muted, fontSize: '.7rem' }} aria-hidden>
                  ▾
                </span>
              </span>
            }
          >
            {(close) => (
              <>
                {/*
                  A LINK OUT, not a settings modal (#63, org-admin#9).

                  Password and MFA belong to the account portal (#10). This
                  portal administers OTHER people's accounts; making it the
                  place you manage your own is the conflation #63 exists to
                  undo. The portal is deployed now and this points at it; the
                  hosted login's reset flow remains the fallback for a bundle
                  built without VITE_ACCOUNT_URL. See lib/auth-client.ts.
                */}
                {/*
                  THE OTHER ACCOUNTS IN THIS BROWSER (up to four), and adding
                  one. The same as ridgewill.com's and the account portal's
                  menus; see lib/device-accounts.ts.
                */}
                {others.length > 0 && <span style={styles.switchLabel}>Switch to</span>}
                {others.map((a) => (
                  <MenuItem
                    key={a.session.token}
                    onClick={() => {
                      close();
                      void switchTo(a.session.token).then(() => window.location.reload());
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <Avatar id={a.user.id} label={a.user.name || a.user.email} image={a.user.image} size={24} />
                      <span style={{ minWidth: 0, lineHeight: 1.25 }}>
                        <span style={styles.switchName}>{a.user.name || a.user.email}</span>
                        {a.user.name ? <span style={styles.switchEmail}>{a.user.email}</span> : null}
                      </span>
                    </span>
                  </MenuItem>
                ))}
                {held && held.length < MAX_ACCOUNTS ? (
                  <MenuItem href={signInUrl()}>Add another account</MenuItem>
                ) : null}
                {held && (others.length > 0 || held.length < MAX_ACCOUNTS) ? (
                  <div style={{ height: 1, background: C.border2, margin: '.15rem 0' }} />
                ) : null}
                <ThemeControl />
                <div style={{ height: 1, background: C.border2, margin: '.15rem 0' }} />
                <MenuItem href={accountSettingsUrl()} external onClick={close}>
                  Account settings
                </MenuItem>
                <MenuItem
                  tone="danger"
                  onClick={() => {
                    close();
                    /**
                     * Say WHY the session is about to be missing.
                     *
                     * `/` cannot otherwise tell "I asked for this" from "a
                     * sign-in round trip came back empty", and the two want
                     * opposite treatment — one carries on to sign-in, the other
                     * must stop or it loops. See lib/bounce.ts, and the account
                     * portal, which this now matches.
                     */
                    /**
                     * With several accounts in this browser, auth's account
                     * chooser asks WHICH to sign out of and comes back here.
                     * `signOut()` ends every one of them.
                     */
                    if (held && held.length > 1) {
                      window.location.assign(chooserUrl(returnQuery()));
                      return;
                    }
                    markSignedOut();
                    void signOut().then(() => window.location.reload());
                  }}
                >
                  Sign out
                </MenuItem>
              </>
            )}
          </Menu>
        </div>
      </nav>

      <div style={styles.column}>
        <div className="rw-mobilebar">
          <button
            type="button"
            className="rw-menubutton"
            aria-controls="rw-sidebar"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
              <path d="M3 5.5h14M3 10h14M3 14.5h14" />
            </svg>
            <span>Menu</span>
            {reportCounts && reportCounts.active > 0 ? (
              <ReportBadge count={reportCounts.active} urgent={needsAttention(reportCounts)} />
            ) : null}
          </button>
          <Logo size={20} />
        </div>
        {/*
          THE HEADER IS A SLOT, not a title bar (org-admin#7).

          It used to hold the section name and the signed-in email, and both were
          redundant: the nav says which screen this is, and the avatar block says
          who you are. What goes here now is the screen's own filter row and
          search — projected in from `components/Screen.tsx` — so the controls sit
          in the chrome rather than pushing the table down the page.

          `ref` rather than a child element: the header IS the portal target, so
          `.rw-header:empty` in styles.css collapses it on a screen that has no
          filters. A permanently empty band with a border under it would be
          chrome that says nothing.
        */}
        <header ref={setHeaderSlot} className="rw-header" />

        {/*
          THE SCROLL CONTAINER, and the reason it moved off the body.

          With the page scrolling, the scrollbar ran the full height of the
          window — and every time it appeared the viewport narrowed by its width,
          so the header reflowed. Confining the scroll here means it starts below
          the header and the header never resizes because of it.

          `data-scroll-root` is how Modal finds the thing to freeze while a
          dialog is open. Locking `body` no longer does anything now that body is
          not what scrolls.
        */}
        <main style={styles.main} className="rw-main" data-scroll-root>
          <Screen section={section} segments={segments} query={query} navigate={navigate} fleetAlerts={fleetAlertState} />
        </main>
      </div>

    </div>
    </HeaderSlot.Provider>
  );
}

/**
 * A nav entry shown only to a session holding at least one of `anyOf`.
 *
 * A component rather than a filter in the loop, because the capabilities come
 * from a context provided INSIDE the shell's own render, which the shell's
 * hooks cannot see. Optimistic while unknown, so the entry appears during the
 * fetch and after a failed one, and only a definite "neither" removes it.
 */
function NavGate({ anyOf, children }: { anyOf?: readonly string[]; children: ReactNode }) {
  const allowed = useCanAny(...(anyOf ?? []));
  return allowed ? <>{children}</> : null;
}

/**
 * A group shown only while at least one of its entries is, so a role left with
 * no developer screens is not left with a "Developer" heading over nothing
 * (org-admin#134). `gates` is each entry's `anyOf`; an ungated entry counts as
 * shown.
 */
function NavGroupGate({
  gates,
  children,
}: {
  gates: (readonly string[] | undefined)[];
  children: ReactNode;
}) {
  const held = useHeldCapabilities();
  return gates.some((g) => holdsAny(held, [...(g ?? [])])) ? <>{children}</> : null;
}

/** Each nav path's `anyOf`, for the screen guard below. */
const SCREEN_GATES = new Map<string, readonly string[]>();
/** Every entry in nav order, for finding a session's home. */
const NAV_ENTRIES: { path: string; anyOf?: readonly string[] }[] = [];
for (const group of NAV_GROUPS) {
  for (const item of group.items as readonly { path: string; anyOf?: readonly string[] }[]) {
    NAV_ENTRIES.push(item);
    if (item.anyOf) SCREEN_GATES.set(item.path, item.anyOf);
  }
}

/**
 * The root, for a session that cannot open the Overview: go to its first
 * screen. `replace`, not a new history entry, so Back does not bounce the
 * person straight back here and on again.
 */
/** Overview, then Legal, then the nav's order. See `homeOrder`. */
const HOME_ENTRIES = homeOrder(NAV_ENTRIES, ['', 'legal']);

function HomeRedirect() {
  const home = firstAllowedPath(useHeldCapabilities(), HOME_ENTRIES);
  useEffect(() => {
    if (home !== null && home !== '') window.location.replace(`#/${home}`);
  }, [home]);
  return home === null ? (
    <p style={styles.muted}>Your role does not include any screen in this portal.</p>
  ) : null;
}

function Screen({
  section,
  segments,
  query,
  navigate,
  fleetAlerts,
}: {
  section: string;
  segments: string[];
  query: Record<string, string>;
  navigate: (to: string) => void;
  fleetAlerts: FleetAlertsState | null;
}) {
  /**
   * THE SAME GATE AS THE NAV, for a screen reached by URL. Hiding the link
   * alone would leave a bookmark or a pasted link opening a screen whose every
   * request the server then refuses, which reads as the portal being broken.
   * One hook call on every render, whatever the section, so hook order holds.
   *
   * A gated screen WAITS for the answer rather than rendering optimistically,
   * because screens act on mount: CI fleet redirects to sign in to fleet-api.
   * A failed capabilities fetch ends the wait and falls back to rendering.
   */
  const gate = screenGate(useCapabilitiesLoading(), useHeldCapabilities(), SCREEN_GATES.get(section));
  if (gate === 'wait') return <p style={styles.muted}>Checking your access…</p>;
  if (gate === 'refuse' && section === '') return <HomeRedirect />;
  if (gate === 'refuse') {
    return (
      <p style={styles.muted}>
        Your role does not include this screen. If you need it, ask an administrator for the role
        that does.
      </p>
    );
  }

  switch (section) {
    case '':
      return <Overview navigate={navigate} />;
    /*
      EVERY RECORD IS `:section/:id[/:tab]`, and every detail view is a SCREEN.
      (Jon, 2026-08-28. See components/Record.tsx for what it cost as a modal.)

      Security groups had this shape first, as the exception: its detail could
      not be a dialog because the members table is paged and searchable. The
      reasoning was never specific to that table — a record is a place, and a
      place needs an address that opens it for anybody, not one that works if
      the reader's search box happens to contain the right row.
    */
    case 'users':
      return <Users id={segments[1]} tab={segments[2]} />;
    /* The same detail view as Users, deliberately — an agent's sessions, roles
       and activity are the questions asked of any account. See Users.tsx. */
    case 'agents':
      return <Agents id={segments[1]} tab={segments[2]} />;
    case 'testers':
      return <Testers />;
    case 'security-groups':
      return <SecurityGroups slug={segments[1]} navigate={navigate} />;
    case 'applications':
      return <Applications id={segments[1]} tab={segments[2]} />;
    case 'agreements':
      /* What Legal was called until 2026-10-15. Links and bookmarks to it
         keep working. */
      return (
        <Redirect
          to={`/legal${segments[1] ? `/${encodeURIComponent(segments[1])}` : ''}`}
          navigate={navigate}
        />
      );
    case 'legal':
      /* No tab: an agreement reads better whole. Jon chose tabs for Users and
         Applications because their concerns are genuinely separate — sessions
         have nothing to do with passkeys — where an agreement's versions and its
         acceptance log are two views of one thing. */
      return <Agreements id={segments[1]} />;
    case 'activity':
      /* `?user=` rather than a path segment: it is a FILTER on this screen, not
         a different thing to look at, and it has to survive alongside the
         screen's own filters — and alongside `segments[1]`, which is now an
         event being read. Keyed on the filter so arriving from a second profile
         remounts rather than keeping the first account's filter state; NOT keyed
         on the event, so drilling into one and coming back leaves the window and
         the date range where they were. */
      return (
        <Activity
          key={query.user ?? 'all'}
          eventId={segments[1]}
          userId={query.user}
          navigate={navigate}
        />
      );
    case 'notifications':
      /* `:section/:id`, with `?address=` as a filter, the Activity screen's
         shape. Keyed on the address so arriving from a second profile's
         recent-sends link remounts rather than keeping the first one's filters. */
      return (
        <Notifications
          key={query.address ?? 'all'}
          section={segments[1]}
          id={segments[2]}
          address={query.address}
          navigate={navigate}
        />
      );
    case 'developer':
      return <Developer id={segments[1]} />;
    case 'system-health':
      /* The section is a PATH segment, not local state (org-admin#57), for the
         same reason a selected account is: an incident link should be able to
         point at "uptime" rather than at "the health screen, then click the
         second tab". */
      return <SystemHealth section={segments[1]} />;
    case 'support':
      /* `#/support` is the queue, `#/support/<reference>` one ticket. */
      return <Support reference={segments[1]} detail={segments[2]} navigate={navigate} />;
    case 'reports':
      /* `#/reports/<section>` or `#/reports/<R-id>` (support#87). */
      return <Reports section={segments[1]} detail={segments[2]} item={segments[3]} query={query} navigate={navigate} />;
    case 'support-configuration':
      /* `#/support-configuration/<section>[/<detail>]`: categories, mapping, settings, suspended IPs. */
      return <SupportConfiguration section={segments[1]} detail={segments[2]} navigate={navigate} />;
    case 'support-settings':
      /* What this page was called for a day (Jon, 2026-10-12). */
      return <Redirect to={movedSupportPath(segments[1], segments[2])} navigate={navigate} />;
    case 'knowledgebase':
      return <Knowledgebase />;
    case 'subscriptions':
      return <Subscriptions section={segments[1]} />;
    case 'ci-fleet':
      /* Sections as path segments, like System health, so a link can point at
         the queue directly (org-admin#96). */
      return <CiFleet section={segments[1]} query={query} navigate={navigate} alerts={fleetAlerts} />;
    default:
      return (
        <p style={styles.muted}>
          No such screen: <code>{section}</code>
        </p>
      );
  }
}

const styles = {
  page: {
    // FIXED to the viewport, so the page itself never scrolls (org-admin#7).
    // Whatever scrolls is inside, which is what keeps a scrollbar from
    // narrowing the viewport and reflowing the header every time it appears.
    height: '100vh',
    overflow: 'hidden',
    background: C.page,
    color: C.text,
    display: 'grid',
    // `max-content` on the sidebar rather than a fixed width repeated in three
    // places: the rail sizes to its longest label, so renaming one does not
    // silently clip it.
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    alignItems: 'stretch',
  },
  /** Header then main, with main taking whatever is left — so `overflow` on
   *  main has a bounded height to scroll within. */
  column: {
    minWidth: 0,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
  },
  sidebar: {
    width: 232,
    height: '100vh',
    // The sidebar itself does NOT scroll — the link list inside it does, so the
    // brand stays pinned at the top and the avatar at the bottom.
    overflow: 'hidden',
    background: C.surface,
    borderRight: `1px solid ${C.border}`,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '.15rem',
    padding: '1.1rem .85rem',
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '6px 8px 20px',
    flex: 'none',
  },
  navScroll: { flex: 1, minHeight: 0, overflowY: 'auto' as const },
  /* The divider, as a border on the group rather than a separate <hr>: one
     element fewer, and it cannot drift away from the group it separates.
     `C.border2` is a token, so it follows the theme — a literal here would be
     an invisible line on the dark sidebar, which is the whole failure this
     portal's dark theme work was about. */
  navGroupDivided: {
    borderTop: `1px solid ${C.border2}`,
    marginTop: 10,
    paddingTop: 10,
  },
  /* Quieter than the links it heads (`C.textMid`), which is the point of a
     group label — but `C.textMid2` rather than `C.muted`, because this is
     persistent structural text somebody reads, not a decorative dot. It
     measures 5.88:1 light and 6.95:1 dark; `muted` would be 2.52 and 4.07. */
  navGroupLabel: {
    fontSize: '.66rem',
    fontWeight: 700,
    letterSpacing: '.07em',
    textTransform: 'uppercase' as const,
    color: C.textMid2,
    padding: '0 .7rem',
    margin: '0 0 6px',
  },
  navLink: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
    gap: 11,
    padding: '.55rem .7rem',
    borderRadius: 9,
    fontSize: '.88rem',
    fontWeight: 500,
    color: C.textMid,
    textDecoration: 'none',
    marginBottom: 3,
    transition: 'background .12s ease, color .12s ease',
  },
  navLinkActive: { background: C.blueTint, color: C.blueText, fontWeight: 640 },
  // The CI fleet alert count (org-admin#97): pushed to the row's end, white on the alert's tone.
  navCount: {
    marginLeft: 'auto',
    minWidth: 20,
    height: 20,
    padding: '0 6px',
    borderRadius: 10,
    color: '#fff',
    fontSize: '.72rem',
    fontWeight: 700,
    lineHeight: '20px',
    textAlign: 'center' as const,
  },
  navRail: {
    position: 'absolute' as const,
    left: 0,
    top: 8,
    bottom: 8,
    width: 3,
    borderRadius: 3,
    background: C.turquoise,
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '.6rem .5rem',
    marginTop: 8,
    borderTop: `1px solid ${C.border2}`,
    paddingTop: 12,
    // Pinned: it is the last flex item and refuses to shrink, so a scrolling
    // nav slides underneath it rather than pushing it off the bottom.
    flex: 'none',
  },
  identityName: {
    display: 'block',
    fontSize: '.82rem',
    fontWeight: 640,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  identityRole: { display: 'block', fontSize: '.75rem', color: C.muted },
  /* `header` is styled in styles.css, not here — see the comment on `.rw-header`.
     An inline `display` would beat the `:empty` rule that collapses it. */
  main: {
    padding: '1.25rem 1.5rem 4rem',
    minWidth: 0,
    // THE scroll container. See the comment where it is rendered.
    flex: 1,
    overflowY: 'auto' as const,
  },
  centre: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    background: C.page,
    color: C.text,
    padding: '2rem',
    textAlign: 'center' as const,
  },
  h1: { fontSize: '1.25rem', fontWeight: 700, margin: 0, letterSpacing: '-.01em' },
  muted: { color: C.muted, lineHeight: 1.5 },
  link: { color: C.brand },
  switchLabel: { display: 'block', padding: '.35rem .7rem .1rem', fontSize: '.72rem', fontWeight: 600, color: C.muted },
  switchName: { display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const },
  switchEmail: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    fontSize: '.72rem',
    color: C.muted,
  },
  signOut: {
    display: 'block',
    width: '100%',
    textAlign: 'left' as const,
    background: 'none',
    border: 'none',
    padding: '.5rem .7rem',
    borderRadius: 9,
    font: 'inherit',
    fontSize: '.82rem',
    color: C.dangerText,
    cursor: 'pointer',
    transition: 'background .12s ease',
  },
};
